import { BrowserRouter, Routes, Route, useLocation, Navigate, useSearchParams } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider } from "@/lib/auth";
import { I18nProvider } from "@/i18n";
import Dispatcher from "@/components/Dispatcher";
import Interrogation from "@/components/Interrogation";
import KairoStings from "@/components/KairoStings";
import SocietyWelcome from "@/components/SocietyWelcome";
import Landing from "@/pages/Landing";
import Streamer from "@/pages/Streamer";
import StreamerDashboard from "@/pages/StreamerDashboard";
import StreamerLive from "@/pages/StreamerLive";
import StreamOverlay from "@/pages/StreamOverlay";
import Live from "@/pages/Live";
import Founding from "@/pages/Founding";
import FoundingSuccess from "@/pages/FoundingSuccess";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Invite from "@/pages/Invite";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import MyKits from "@/pages/MyKits";
import ForTeams from "@/pages/ForTeams";
import ScreenKit from "@/pages/ScreenKit";
import { Terms, Privacy, Refunds } from "@/pages/Legal";
import SecretSantaOffice from "@/pages/SecretSantaOffice";
import Crowdfund from "@/pages/Crowdfund";
import QuotePreview from "@/pages/QuotePreview";
import RecapPage from "@/pages/RecapPage";
import PlayerScreen from "@/pages/PlayerScreen";
import HostStage from "@/pages/HostStage";
import Gifts from "@/pages/Gifts";
import GiftBalance from "@/pages/GiftBalance";
import GiftThanks from "@/pages/GiftThanks";
import Night from "@/pages/Night";
import NightPlay from "@/pages/NightPlay";
import Daily from "@/pages/Daily";
import Plan from "@/pages/Plan";
import Follow from "@/pages/Follow";
import Library from "@/pages/Library";
import CrewRoom from "@/pages/CrewRoom";
import CrewThanks from "@/pages/CrewThanks";
import Make from "@/pages/Make";
import MakeDownload from "@/pages/MakeDownload";
import Founder, { FounderThanks } from "@/pages/Founder";
import Members from "@/pages/Members";
import Club, { ClubThanks, ClubMember } from "@/pages/Club";
import Teaser from "@/pages/Teaser";
import AdminNye from "@/pages/AdminNye";
import Leaderboard from "@/pages/Leaderboard";
import LeaderboardHowItWorks from "@/pages/LeaderboardHowItWorks";
import WhodunitWeek from "@/pages/WhodunitWeek";
import WhodunitVerdict from "@/pages/WhodunitVerdict";
import Packs from "@/pages/Packs";
import PackSuccess from "@/pages/PackSuccess";
import License, { LicenseThanks } from "@/pages/License";
import Commission from "@/pages/Commission";
import Support from "@/pages/Support";
import Challenge from "@/pages/Challenge";
import StreamerPlaybook from "@/pages/StreamerPlaybook";
import StreamerOutreach from "@/pages/StreamerOutreach";
import PackComp from "@/pages/PackComp";
import Society from "@/pages/Society";
import Enterprise from "@/pages/Enterprise";
import EnterpriseAuth from "@/pages/EnterpriseAuth";
import EnterpriseSetup from "@/pages/EnterpriseSetup";
import EnterpriseDashboard from "@/pages/EnterpriseDashboard";
import EnterpriseEvent from "@/pages/EnterpriseEvent";
import EnterpriseSuccess from "@/pages/EnterpriseSuccess";
import EnterpriseProposal from "@/pages/EnterpriseProposal";
import EnterpriseJoin from "@/pages/EnterpriseJoin";

// Shelf 1 — the recipient's email link lands here; forward them into /checkout
// with the code pre-populated. Balance page remains the fallback if they just
// want to see what's on it first.
const GiftRedeemRedirect = () => {
  const [p] = useSearchParams();
  const code = (p.get("code") || "").toUpperCase();
  return <Navigate to={`/gifts/balance?code=${encodeURIComponent(code)}`} replace />;
};

const AppRouter = () => {
  const location = useLocation();
  // Detect OAuth session_id in URL fragment synchronously (not useEffect — too late)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const isEnterprise = /^\/(enterprise|e)(\/|$)/.test(location.pathname);  return (
    <>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/invite/:groupId" element={<Invite />} />
      <Route path="/dashboard/:hostToken" element={<Dashboard />} />
      <Route path="/my-kits" element={<MyKits />} />
      <Route path="/for-teams" element={<ForTeams />} />
      <Route path="/for-teams/secret-santa-office" element={<SecretSantaOffice />} />
      <Route path="/kit/:hostToken" element={<ScreenKit />} />
      <Route path="/crowdfund" element={<Crowdfund />} />
      <Route path="/preview/:quoteId" element={<QuotePreview />} />
      <Route path="/recap/:hostToken" element={<RecapPage />} />
      <Route path="/play/:playerToken" element={<PlayerScreen />} />
      <Route path="/stage/:hostToken" element={<HostStage />} />
      <Route path="/gifts" element={<Gifts />} />
      <Route path="/gifts/balance" element={<GiftBalance />} />
      <Route path="/gifts/thanks/:cardId" element={<GiftThanks />} />
      <Route path="/gifts/redeem" element={<GiftRedeemRedirect />} />
      <Route path="/night" element={<Night />} />
      <Route path="/night/play/:signupId" element={<NightPlay />} />
      <Route path="/daily" element={<Daily />} />
      <Route path="/plan" element={<Plan />} />
      <Route path="/follow" element={<Follow />} />
      <Route path="/library" element={<Library />} />
      <Route path="/night/crew/thanks" element={<CrewThanks />} />
      <Route path="/night/crew/:crewToken" element={<CrewRoom />} />
      <Route path="/make" element={<Make />} />
      <Route path="/make/download/:orderId" element={<MakeDownload />} />
      <Route path="/founder" element={<Founder />} />
      <Route path="/founder/thanks" element={<FounderThanks />} />
      <Route path="/members/:memberToken" element={<Members />} />
      <Route path="/club" element={<Club />} />
      <Route path="/club/thanks" element={<ClubThanks />} />
      <Route path="/club/members/:token" element={<ClubMember />} />
      <Route path="/teaser" element={<Teaser />} />
      <Route path="/admin/nye" element={<AdminNye />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/leaderboard/how-it-works" element={<LeaderboardHowItWorks />} />
      <Route path="/whodunit-week" element={<WhodunitWeek />} />
      <Route path="/whodunit-week/verdict/:token" element={<WhodunitVerdict />} />
      <Route path="/packs" element={<Packs />} />
      <Route path="/packs/success" element={<PackSuccess />} />
      <Route path="/license" element={<License />} />
      <Route path="/license/thanks" element={<LicenseThanks />} />
      <Route path="/commission" element={<Commission />} />
      <Route path="/support" element={<Support />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/streamer" element={<Streamer />} />
      <Route path="/streamer/dashboard" element={<StreamerDashboard />} />
      <Route path="/streamer/live" element={<StreamerLive />} />
      <Route path="/stream/overlay/:code" element={<StreamOverlay />} />
      <Route path="/live/:code" element={<Live />} />
      <Route path="/founding" element={<Founding />} />
      <Route path="/founding/success" element={<FoundingSuccess />} />
      <Route path="/challenge" element={<Challenge />} />
      <Route path="/streamer/playbook" element={<StreamerPlaybook />} />
      <Route path="/streamer/outreach" element={<StreamerOutreach />} />
      <Route path="/packs/comp/:token" element={<PackComp />} />
      <Route path="/society" element={<Society />} />
      <Route path="/enterprise" element={<Enterprise />} />
      <Route path="/enterprise/login" element={<EnterpriseAuth />} />
      <Route path="/enterprise/setup" element={<EnterpriseSetup />} />
      <Route path="/enterprise/dashboard" element={<EnterpriseDashboard />} />
      <Route path="/enterprise/events/:eventId" element={<EnterpriseEvent />} />
      <Route path="/enterprise/success" element={<EnterpriseSuccess />} />
      <Route path="/enterprise/proposal" element={<EnterpriseProposal />} />
      <Route path="/e" element={<EnterpriseJoin />} />
      <Route path="/e/:code" element={<EnterpriseJoin />} />
    </Routes>
    {!isEnterprise && (
      <>
        <Dispatcher />
        <Interrogation />
        <KairoStings />
        <SocietyWelcome />
      </>
    )}
    </>
  );
};

function App() {
  return (
    <>
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" />
    </>
  );
}

export default App;
